/**
 * AnalyticsPage — `/analytics` (design: Teacher "Analitika" artboard)
 *
 * Exam selector chips · stat row (average, passed, failed, avg time) ·
 * score-distribution histogram · hardest-5 questions · students × questions
 * heatmap. Data: GET /api/exams/{id}/analytics/.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { listExams, getExamAnalytics } from '@/api/resources'
import { fetchAllPages } from '@/utils/fetchAll'
import { Hex, SectionHeader } from '@/components/kit'
import { Star, Check, X, Clock, BarChart3 } from 'lucide-react'

function fmtMin(seconds) {
  return `${Math.round(seconds / 60)}d`
}

function Histogram({ bins }) {
  const max = Math.max(1, ...bins.map((b) => b.count))
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 200, padding: '8px 0' }}>
      {bins.map((b, i) => {
        const isPeak = b.count === max && max > 0
        return (
          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
            <div className="t-xs fw-7" style={{ marginBottom: 6, color: isPeak ? 'var(--coral)' : 'var(--ink-2)' }}>{b.count}</div>
            <div style={{
              height: `${(b.count / max) * 160}px`,
              background: isPeak ? 'var(--coral)' : 'var(--coral-soft)',
              borderRadius: '10px 10px 4px 4px',
              transition: 'all .3s',
            }} />
            <div className="t-xs t-mut" style={{ marginTop: 6 }}>{b.range}</div>
          </div>
        )
      })}
    </div>
  )
}

function Heatmap({ matrix }) {
  if (!matrix.length) return null
  const cols = Math.max(...matrix.map((r) => r.cells.length))
  return (
    <div className="scroll-y" style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: Math.max(320, cols * 14 + 80) }}>
        {matrix.map((row) => (
          <div key={row.student_id} style={{ display: 'flex', marginTop: 2, alignItems: 'center' }}>
            <div className="fw-6" style={{
              width: 80, fontSize: 10, color: 'var(--ink-2)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 6,
            }}>{row.full_name}</div>
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, gap: 2 }}>
              {row.cells.map((c, i) => {
                const v = c.ratio
                const color = v > 0.65 ? 'var(--mint)' : v > 0.4 ? 'var(--butter)' : 'var(--coral)'
                return <div key={i} title={`#${c.question_id} · ${Math.round(v * 100)}%`}
                  style={{ height: 14, borderRadius: 3, background: color, opacity: 0.35 + v * 0.65 }} />
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function AnalyticsPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [exams, setExams] = useState([])
  const [selected, setSelected] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const fetchedRef = useRef(false)

  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    fetchAllPages(listExams).then(list => {
      setExams(list)
      const paramCode = searchParams.get('exam')
      const initial = paramCode || list[0]?.code
      if (initial) setSelected(initial)
    })
  }, [])

  useEffect(() => {
    if (!selected) return
    setLoading(true)
    getExamAnalytics(selected)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [selected])

  return (
    <div>
      <SectionHeader
        title={t('analytics.title', 'Analitika')}
        subtitle={data ? `${data.title} · ${data.participants} ${t('analytics.students', 'talaba')}` : ''}
      />


      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--coral-soft)', borderTopColor: 'var(--coral)' }} />
        </div>
      )}

      {!loading && data && (
        <>
          {/* Stat row */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {[
              { l: t('analytics.avg', "O'rtacha natija"), v: `${data.average_percent}%`, tone: 'coral', Icon: Star },
              { l: t('analytics.passed', "O'tdi"), v: `${data.passed} / ${data.participants}`, tone: 'mint', Icon: Check },
              { l: t('analytics.failed', 'Yiqildi'), v: `${data.failed} / ${data.participants}`, tone: 'pink', Icon: X },
              { l: t('analytics.avgTime', "O'rt. vaqt"), v: fmtMin(data.average_duration_seconds), tone: 'butter', Icon: Clock },
            ].map((s, i) => (
              <div key={i} className="card" style={{ padding: 16, borderRadius: 18 }}>
                <Hex size="sm" icon={s.Icon} bg={`var(--${s.tone}-soft)`} fg={`var(--${s.tone})`} />
                <div className="t-xs t-mut" style={{ marginTop: 8 }}>{s.l}</div>
                <div className="h-display" style={{ fontSize: 22 }}>{s.v}</div>
              </div>
            ))}
          </div>

          {data.participants === 0 && (
            <div className="card" style={{ padding: 32, borderRadius: 22, textAlign: 'center', borderStyle: 'dashed', boxShadow: 'none' }}>
              <BarChart3 size={36} strokeWidth={1.5} style={{ margin: '0 auto 10px', color: 'var(--muted)' }} />
              <div className="t-sm t-mut">{t('analytics.empty', "Bu imtihonda hali tugatilgan natijalar yo'q.")}</div>
            </div>
          )}

          {data.participants > 0 && (
            <>
              <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4">
                {/* Histogram */}
                <div className="card" style={{ padding: 20, borderRadius: 22 }}>
                  <SectionHeader title={t('analytics.distribution', 'Ball taqsimoti')} />
                  <Histogram bins={data.histogram} />
                </div>

                {/* Hardest questions */}
                <div className="card" style={{ padding: 20, borderRadius: 22 }}>
                  <SectionHeader title={t('analytics.hardest', 'Eng qiyin 5 savol')} />
                  {data.hardest_questions.map((q, i) => {
                    const tone = q.success_percent < 40 ? 'pink' : 'butter'
                    return (
                      <div key={q.question_id} className="row gap-12" style={{ padding: '10px 0', borderTop: i ? '1px solid var(--border)' : 0 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                          background: `var(--${tone}-soft)`, color: `var(--${tone})`,
                          display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 11,
                        }}>#{q.question_id}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="fw-7 t-sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.content}</div>
                          <div className="prog" style={{ marginTop: 4 }}>
                            <i style={{ width: `${q.success_percent}%`, background: `var(--${tone})` }} />
                          </div>
                        </div>
                        <span className="fw-7 t-sm" style={{ color: `var(--${tone})` }}>{q.success_percent}%</span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Heatmap */}
              <div className="card" style={{ padding: 20, borderRadius: 22, marginTop: 16 }}>
                <SectionHeader
                  title={t('analytics.matrix', 'Talabalar × savollar')}
                  subtitle={t('analytics.matrixHint', "Yashil — to'g'ri, sariq — qisman, qizil — noto'g'ri")}
                />
                <Heatmap matrix={data.matrix} />
              </div>
            </>
          )}
        </>
      )}

      {!loading && !data && exams.length === 0 && (
        <div className="card" style={{ padding: 32, borderRadius: 22, textAlign: 'center', borderStyle: 'dashed', boxShadow: 'none' }}>
          <BarChart3 size={36} strokeWidth={1.5} style={{ margin: '0 auto 10px', color: 'var(--muted)' }} />
          <div className="t-sm t-mut">{t('analytics.noExams', "Hali imtihonlar yo'q.")}</div>
        </div>
      )}
    </div>
  )
}
